import type {
  ApiResponse,
  ApiResponseEnvelope,
  PaginatedResponse,
} from '@/shared/api/types/api-response'
import type {
  CountryListParams,
  ICountry,
  IProvince,
  ProvinceListParams,
} from '@/shared/api/types/country-types'
import { GW, toQueryString } from '@/shared/api/endpoints'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { DEFAULT_FETCH_LIMIT } from '@/shared/constants/pagination'
import { request } from '@/shared/libs/request'

const COUNTRIES_PATH = 'countries'
const PROVINCES_PATH = 'provinces'

export async function getCountries(
  params: CountryListParams = {},
): Promise<ApiResponse<ICountry[]>> {
  const qs = toQueryString({
    ...(params.keyword ? { keyword: params.keyword.trim() } : {}),
    current: 1,
    pageSize: DEFAULT_FETCH_LIMIT,
  })
  const response = await request.get<ApiResponseEnvelope<PaginatedResponse<ICountry>>>(
    GW.masterdata(`${COUNTRIES_PATH}?${qs}`),
  )
  const data = unwrapApiEnvelope(response.data)
  return { data: data.data ?? [] }
}

export async function getProvinces(params: ProvinceListParams): Promise<ApiResponse<IProvince[]>> {
  if (!params.countryId) {
    return { data: [] }
  }

  const qs = toQueryString({ current: 1, pageSize: DEFAULT_FETCH_LIMIT })
  const response = await request.get<ApiResponseEnvelope<PaginatedResponse<IProvince>>>(
    GW.masterdata(`${COUNTRIES_PATH}/${params.countryId}/${PROVINCES_PATH}?${qs}`),
  )
  const data = unwrapApiEnvelope(response.data)
  return { data: data.data ?? [] }
}

export async function getAllProvinces(countryIds: string[]): Promise<ApiResponse<IProvince[]>> {
  if (countryIds.length === 0) {
    return { data: [] }
  }
  const provinceMap = new Map<string, IProvince>()
  const batchSize = 5

  for (let index = 0; index < countryIds.length; index += batchSize) {
    const batch = countryIds.slice(index, index + batchSize)
    const responses = await Promise.all(batch.map(async (countryId) => getProvinces({ countryId })))
    responses.forEach((response) => {
      response.data.forEach((item) => {
        provinceMap.set(item.id, item)
      })
    })
  }

  return {
    data: Array.from(provinceMap.values()),
  }
}
